/**
 * Unit 1.4 · IMPL.C: delete confirmation with honest blast-radius copy
 * (replaces the legacy MUI Modal version).
 *
 * Bead: insurescope-11zkc.2.4.3.3 (F5). Deleting a client does NOT delete
 * its content: `crud_client.delete()` clears `client_id` on documents/
 * folders and the `SET NULL` FKs unlink scopes, Polly conversations, and
 * comparisons. The amber callout says exactly that. Documents/scopes are
 * numbered (org-wide counts, true); Polly chats are mentioned QUALITATIVELY
 * only, because `conversation_count` is per-user (F1) and would undercount
 * what a delete actually unlinks (it SET-NULLs every user's chats).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
    deleteClient,
    getApiErrorMessage,
    type IApiAxiosError,
    type IClient,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

import { pluralCount } from "../utils/pluralCount";

export interface DeleteClientDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    client: IClient;
}

export function DeleteClientDialog({
    open,
    onOpenChange,
    client,
}: DeleteClientDialogProps) {
    const queryClient = useQueryClient();
    const [apiError, setApiError] = useState<string | null>(null);

    // Org-wide counts only; the per-user conversation_count must not gate
    // or number the warning.
    const hasAssociations = client.document_count > 0 || client.scope_count > 0;

    const { mutate, isPending } = useMutation({
        mutationFn: () => deleteClient(client.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["clients"] });
            queryClient.invalidateQueries({ queryKey: ["clients-stats"] });
            toast.success("Client deleted");
            onOpenChange(false);
        },
        onError: (err: IApiAxiosError) => {
            setApiError(getApiErrorMessage(err, "Failed to delete client"));
        },
    });

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (isPending) return;
                setApiError(null);
                onOpenChange(next);
            }}
        >
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>Delete client?</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-3 text-sm">
                    <p>
                        Are you sure you want to delete{" "}
                        <span className="font-semibold">{client.name}</span>?
                    </p>
                    {hasAssociations ? (
                        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                            {client.name} has{" "}
                            {pluralCount(client.document_count, "document")} and{" "}
                            {pluralCount(client.scope_count, "scope")}.
                            They&apos;ll stay in your library but lose their
                            link to this client, and any Polly chats will be
                            unlinked too.
                        </p>
                    ) : (
                        <p className="text-muted-foreground">
                            This can&apos;t be undone.
                        </p>
                    )}
                    {apiError && <p className="text-destructive">{apiError}</p>}
                </div>

                <DialogFooter>
                    <Button
                        autoFocus
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => {
                            if (isPending) return;
                            mutate();
                        }}
                        disabled={isPending}
                    >
                        {isPending && (
                            <Loader2 className="size-4 animate-spin" />
                        )}
                        Delete Client
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
